/**
 * Shared candidate UI components (v3 CandidateProfileView cards).
 * CandidateProfileView composes these for the project candidate page, the
 * Smart Scout candidate modal and the Discover Talent profile page.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useCandidateCv } from "../../hooks/useCandidateCv";
import { useAnimatedScore } from "../../hooks/useAnimatedScore";
import type {
  CandidateAward,
  CandidateDetail,
  CandidateSkill,
  EmploymentHistoryItem,
  EducationItem,
  CandidateStatusEnum,
} from "../../types/candidate";
import { STATUS_CONFIG } from "../../types/candidate";
import { cn, toTitleCase } from "../../lib/utils";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { EmptyState } from "../ui/EmptyState";
import { StatusPill } from "../ui/StatusPill";
import { MarkdownContent } from "../chat/MarkdownContent";
import CopyLinear from "~icons/solar/copy-linear";
import CheckCircleLinear from "~icons/solar/check-circle-linear";
import StarsMinimalisticLinear from "~icons/solar/stars-minimalistic-linear";
import DiplomaLinear from "~icons/solar/diploma-linear";
import CaseLinear from "~icons/solar/case-linear";
import CupFirstLinear from "~icons/solar/cup-first-linear";
import DocumentTextLinear from "~icons/solar/document-text-linear";
import DownloadLinear from "~icons/solar/download-linear";
import RestartLinear from "~icons/solar/restart-linear";
import LetterLinear from "~icons/solar/letter-linear";
import PhoneLinear from "~icons/solar/phone-linear";
import MapPointLinear from "~icons/solar/map-point-linear";
import { formatDate, getScoreColor, sortEmploymentByDate } from "./candidate-utils";
import { Spinner } from "../ui/Spinner";
import { Icon } from "../ui/Icon";
import DownloadMinimalisticLinear from "~icons/solar/download-minimalistic-linear";
import AltArrowLeftLinear from "~icons/solar/alt-arrow-left-linear";
import AltArrowRightLinear from "~icons/solar/alt-arrow-right-linear";
import MagnifierZoomInLinear from "~icons/solar/magnifer-zoom-in-linear";
import MagnifierZoomOutLinear from "~icons/solar/magnifer-zoom-out-linear";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure pdf.js worker — use unpkg CDN matching the exact bundled version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ---------------------------------------------------------------------------
// CollapsibleContent — CSS grid-rows animation for smooth expand/collapse
// ---------------------------------------------------------------------------

export function CollapsibleContent({
  open,
  children,
  className,
}: {
  open: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`grid motion-safe:transition-[grid-template-rows] motion-safe:duration-300 motion-safe:ease-in-out ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      } ${className ?? ""}`}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScoreDisplay
// ---------------------------------------------------------------------------

export interface ScoreDisplayProps {
  label: string;
  score: number | undefined;
}

export function ScoreDisplay({ label, score }: ScoreDisplayProps) {
  const animatedValue = useAnimatedScore(score ?? 0);
  const hasScore = score !== undefined && score !== null;

  return (
    <div className="flex flex-row items-center justify-between gap-3">
      <span className="text-xs text-ink-muted">{label}</span>
      {hasScore ? (
        <span className={`text-base font-bold leading-tight tabular-nums ${getScoreColor(score)}`}>
          {animatedValue}%
        </span>
      ) : (
        <span className="text-base font-bold leading-tight text-ink-faint">—</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

export interface StatusBadgeProps {
  status: CandidateStatusEnum;
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  return <StatusPill status={config?.label ?? status} tone={config?.color ?? "grey"} />;
}

// ---------------------------------------------------------------------------
// TabBar — generic ARIA-compliant tab bar
// ---------------------------------------------------------------------------

export interface TabConfig {
  id: string;
  label: string;
}

export interface TabBarProps {
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (id: string) => void;
  /** aria-label for the tablist container. */
  ariaLabel?: string;
}

export function TabBar({
  tabs,
  activeTab,
  onTabChange,
  ariaLabel = "Tabs",
}: TabBarProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = tabs.findIndex((t) => t.id === activeTab);
    let nextIndex = currentIndex;

    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    onTabChange(tabs[nextIndex].id);
  };

  return (
    <div
      className="inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-canvas-subtle/70 p-1"
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              "rounded-full px-5 py-2 text-xs font-semibold motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              isActive
                ? "bg-white text-ink shadow-card"
                : "text-ink-muted hover:bg-white/40 hover:text-ink"
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AnimatedSection — staggered entrance animation wrapper
// ---------------------------------------------------------------------------

export function AnimatedSection({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  return (
    <div
      className="section-stagger-in"
      style={{ "--stagger-delay": `${index * 75}ms` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkillChip + AttributesSection (v3 "Attributes" card: flat chip list)
// ---------------------------------------------------------------------------

export function SkillChip({ skill }: { skill: CandidateSkill }) {
  return (
    <span className="rounded-full border border-black/[0.06] bg-surface-3 px-3 py-1 text-xs font-medium text-ink-soft transition hover:border-primary/30 hover:text-primary">
      {skill.name}
    </span>
  );
}

export interface AttributesSectionProps {
  items: CandidateSkill[];
}

export function AttributesSection({ items }: AttributesSectionProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="border-b border-black/[0.06] pb-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-ink">Attributes</h3>
      </div>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map((skill) => (
            <SkillChip key={skill.id ?? skill.name} skill={skill} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-ink-muted">No attributes available.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MatchToJobCard — "Match to Job Description" / "Profile Summary & Highlights"
// ---------------------------------------------------------------------------

export interface MatchToJobCardProps {
  /** Card title — project: "Match to Job Description", discover: "Profile Summary & Highlights". */
  title?: string;
  content?: string | null;
  /** Rendered instead of the empty copy (e.g. "Generating AI summary…"). */
  placeholder?: ReactNode;
}

export function MatchToJobCard({
  title = "Match to Job Description",
  content,
  placeholder,
}: MatchToJobCardProps) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const explanation = content || "";

  const handleCopy = useCallback(async () => {
    if (!explanation) return;
    try {
      await navigator.clipboard.writeText(explanation);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      setCopied(true);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  }, [explanation]);

  return (
    <Card padding="lg" className="rounded-4xl sm:p-7">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] pb-3.5">
        <div className="flex items-center gap-2 text-sm font-bold text-primary">
          <StarsMinimalisticLinear aria-hidden="true" className="size-[18px]" />
          <h4>{title}</h4>
        </div>
        {explanation && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={copied ? CheckCircleLinear : CopyLinear}
            onClick={handleCopy}
            aria-label="Copy summary"
            className="h-7 px-3.5 text-[11px]"
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        )}
      </div>

      {/* Body */}
      {explanation ? (
        <MarkdownContent
          content={explanation}
          className="mt-4 text-xs leading-relaxed text-ink-soft sm:text-sm [&_p]:text-xs [&_p]:text-ink-soft sm:[&_p]:text-sm [&_li]:text-xs [&_li]:text-ink-soft sm:[&_li]:text-sm [&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:text-sm [&_h1]:font-bold [&_h1]:text-ink [&_h1]:first:mt-0 [&_h2]:mb-1.5 [&_h2]:mt-2.5 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-ink [&_h2]:first:mt-0 [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-ink [&_h3]:first:mt-0 [&_p]:mb-2 [&_p]:last:mb-0"
        />
      ) : placeholder ? (
        <div className="mt-4">{placeholder}</div>
      ) : (
        <p className="mt-4 text-xs text-ink-muted">No summary available yet.</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// WorkExperienceCard
// ---------------------------------------------------------------------------

function experiencePeriod(item: EmploymentHistoryItem): string {
  return item.is_current
    ? `${formatDate(item.start_date)} – Present`
    : item.end_date
      ? `${formatDate(item.start_date)} – ${formatDate(item.end_date)}`
      : formatDate(item.start_date);
}

export interface WorkExperienceCardProps {
  items: EmploymentHistoryItem[];
}

export function WorkExperienceCard({ items }: WorkExperienceCardProps) {
  const sorted = useMemo(() => sortEmploymentByDate(items), [items]);

  return (
    <Card padding="lg" className="rounded-4xl sm:p-7">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] pb-3.5">
        <h4 className="text-base font-bold tracking-tight text-ink">Work Experience</h4>
        {items.length > 0 && (
          <span className="text-xs text-ink-muted tabular-nums">
            {items.length} {items.length === 1 ? "milestone" : "milestones"}
          </span>
        )}
      </div>

      {items.length > 0 ? (
        <div className="mt-5 flex flex-col gap-4">
          {sorted.map((item) => {
            const hasAchievements = item.achievements && item.achievements.length > 0;

            return (
              <div
                key={item.id}
                className="flex flex-col gap-3 rounded-2xl border border-black/[0.04] bg-surface-2 p-4 transition hover:border-black/10 hover:bg-white hover:shadow-xs sm:p-5"
              >
                <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 items-center gap-3">
                    <div
                      className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-black/10 bg-white text-primary shadow-xs"
                      aria-hidden="true"
                    >
                      <CaseLinear className="size-[18px]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold leading-tight text-ink">
                        {item.job_title ? toTitleCase(item.job_title) : "—"}
                      </p>
                      <p className="mt-0.5 text-xs font-semibold text-ink-muted">
                        {item.company_name ? toTitleCase(item.company_name) : ""}
                      </p>
                    </div>
                  </div>
                  <span className="self-start whitespace-nowrap rounded-full border border-black/[0.06] bg-white px-3 py-1 text-xs font-semibold text-ink-muted sm:self-center">
                    {experiencePeriod(item)}
                  </span>
                </div>

                {item.description && (
                  <MarkdownContent
                    content={item.description}
                    className="text-xs leading-relaxed text-ink-soft [&_p]:mb-1.5 [&_p]:text-xs [&_p]:text-ink-soft [&_p]:last:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:text-xs [&_li]:text-ink-soft"
                  />
                )}

                {hasAchievements && (
                  <ul className="list-disc space-y-1.5 pl-5 text-xs text-ink-muted">
                    {item.achievements.map((achievement, achievementIndex) => (
                      <li key={achievementIndex} className="leading-relaxed">
                        {achievement}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-4 text-xs text-ink-muted">No work experience data available.</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Education helpers + EducationCard (v3: degree · school · honors · years)
// ---------------------------------------------------------------------------

const DEGREE_LABELS: Record<string, string> = {
  high_school: "High School Diploma",
  associate: "Associate Degree",
  bachelor: "Bachelor",
  master: "Master",
  doctorate: "Doctorate",
  professional: "Professional Degree",
  certificate: "Certificate",
  diploma: "Diploma",
  other: "",
};

function yearOf(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : String(d.getFullYear());
}

/** "Bachelor of Graphic Design" / "Certificate in Project Management" / institution fallback. */
export function educationTitle(item: EducationItem): string {
  const degree = item.degree
    ? (DEGREE_LABELS[item.degree] ?? toTitleCase(item.degree))
    : "";
  const field = item.field_of_study ? toTitleCase(item.field_of_study) : "";
  if (degree && field) {
    return degree === "Bachelor" || degree === "Master"
      ? `${degree} of ${field}`
      : `${degree} in ${field}`;
  }
  return degree || field || item.institution_name?.trim() || "—";
}

/** "2012 - 2016" / "2016" / null. */
export function educationPeriod(item: EducationItem): string | null {
  const start = yearOf(item.start_date);
  const end = yearOf(item.end_date);
  if (start && end) return start === end ? start : `${start} - ${end}`;
  return end ?? start;
}

function educationSchool(item: EducationItem, title: string): string | null {
  if (!item.institution_name) return null;
  const school = item.institution_name.trim();
  return school === title ? null : school;
}

function EducationRow({ item }: { item: EducationItem }) {
  const title = educationTitle(item);
  const school = educationSchool(item, title);
  const period = educationPeriod(item);

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-black/[0.04] bg-surface-2 p-4">
      <div className="flex min-w-0 items-center gap-3">
        <div
          className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-black/10 bg-white text-ink-soft shadow-xs"
          aria-hidden="true"
        >
          <DiplomaLinear className="size-[18px]" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-tight text-ink">{title}</p>
          {school && <p className="mt-0.5 text-xs text-ink-muted">{school}</p>}
          {item.achievements && (
            <p className="mt-0.5 text-[11px] font-semibold text-primary">{item.achievements}</p>
          )}
        </div>
      </div>
      {period && (
        <span className="shrink-0 whitespace-nowrap rounded-full border border-black/[0.06] bg-white px-3 py-1 text-xs font-semibold text-ink-muted">
          {period}
        </span>
      )}
    </div>
  );
}

export interface EducationCardProps {
  items: EducationItem[];
}

export function EducationCard({ items }: EducationCardProps) {
  return (
    <Card padding="lg" className="rounded-4xl sm:p-7">
      <div className="border-b border-black/[0.06] pb-3.5">
        <h4 className="text-base font-bold tracking-tight text-ink">Education</h4>
      </div>

      {items.length > 0 ? (
        <div className="mt-4 flex flex-col gap-3">
          {items.map((item) => (
            <EducationRow key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs text-ink-muted">No education data available.</p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AwardsCard (v3: cup icon · title · issuer · year)
// ---------------------------------------------------------------------------

export interface AwardsCardProps {
  items: CandidateAward[];
}

export function AwardsCard({ items }: AwardsCardProps) {
  return (
    <Card padding="lg" className="rounded-4xl sm:p-7">
      <div className="border-b border-black/[0.06] pb-3.5">
        <h4 className="text-base font-bold tracking-tight text-ink">Awards</h4>
      </div>

      {items.length > 0 ? (
        <div className="mt-4 flex flex-col gap-3">
          {items.map((award) => {
            const year = yearOf(award.date);
            const subtitle = award.issuer || award.description;
            return (
              <div
                key={award.id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-black/[0.04] bg-surface-2 p-4"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex size-10 shrink-0 items-center justify-center rounded-xl border border-black/10 bg-white text-amber-500 shadow-xs"
                    aria-hidden="true"
                  >
                    <CupFirstLinear className="size-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold leading-tight text-ink">{award.title}</p>
                    {subtitle && <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>}
                  </div>
                </div>
                {year && (
                  <span className="shrink-0 whitespace-nowrap rounded-full border border-black/[0.06] bg-white px-3 py-1 text-xs font-semibold text-ink-muted">
                    {year}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState
          compact
          icon={CupFirstLinear}
          title="No awards yet"
          description="Awards and honours will appear here once they are captured."
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ResumeTemplate — Resume tab (v3 executive CV template + Download CV)
// ---------------------------------------------------------------------------

export interface ResumeTemplateProps {
  data: CandidateDetail;
  candidateName: string;
  /** Executive summary paragraph (AI profile summary / match summary). */
  summary?: string | null;
}

function ResumeSectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2
      className={cn(
        "border-b border-black/[0.06] pb-2 text-xs font-bold uppercase tracking-wider text-ink",
        className
      )}
    >
      {children}
    </h2>
  );
}

export function ResumeTemplate({ data, candidateName, summary }: ResumeTemplateProps) {
  const { blobUrl, isLoading, isError, download } = useCandidateCv(data.id);
  const experience = useMemo(
    () => sortEmploymentByDate(data.employment_history_items),
    [data.employment_history_items]
  );
  const role = data.current_job_title ? toTitleCase(data.current_job_title) : null;
  const canDownload = !isLoading && !isError && !!blobUrl;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      {/* Top action toolbar */}
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-black/[0.08] bg-white px-5 py-3 shadow-xs">
        <div className="flex min-w-0 items-center gap-2">
          <DocumentTextLinear aria-hidden="true" className="size-5 shrink-0 text-primary" />
          <span className="truncate text-xs font-bold uppercase tracking-wider text-ink">
            {candidateName}&apos;s Official Resume / CV
          </span>
        </div>
        <button
          type="button"
          onClick={() => download(candidateName)}
          disabled={!canDownload}
          title={isError ? "No CV on file for this candidate" : undefined}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white shadow-xs transition-all duration-150 hover:scale-105 hover:bg-primary-hover active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {isLoading ? (
            <RestartLinear aria-hidden="true" className="size-3.5 animate-spin" />
          ) : (
            <DownloadLinear aria-hidden="true" className="size-3.5" />
          )}
          <span>{isLoading ? "Preparing PDF..." : "Download CV"}</span>
        </button>
      </div>

      {/* Resume paper */}
      <div className="flex flex-col gap-8 rounded-2xl border border-black/10 bg-white p-6 text-ink shadow-sm sm:p-10">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 border-b border-black/[0.08] pb-6 sm:flex-row sm:items-center">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-ink sm:text-3xl">{candidateName}</h1>
            {role && <p className="mt-1 text-base font-semibold text-primary">{role}</p>}
          </div>
          <div className="flex flex-col gap-1.5 text-xs text-ink-muted sm:items-end">
            <span className="flex items-center gap-1.5">
              <LetterLinear aria-hidden="true" className="size-3.5 text-ink-faint" />
              <span className="break-all">{data.email || "—"}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <PhoneLinear aria-hidden="true" className="size-3.5 text-ink-faint" />
              <span>{data.phone || "—"}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <MapPointLinear aria-hidden="true" className="size-3.5 text-ink-faint" />
              <span>{data.address || "—"}</span>
            </span>
          </div>
        </div>

        {/* Executive Summary */}
        <div>
          <ResumeSectionTitle className="mb-3">Executive Summary</ResumeSectionTitle>
          {summary ? (
            <MarkdownContent
              content={summary}
              className="text-sm leading-relaxed text-ink-soft [&_p]:mb-2 [&_p]:text-ink-soft [&_p]:last:mb-0 [&_li]:text-ink-soft"
            />
          ) : (
            <p className="text-sm text-ink-muted">No summary available yet.</p>
          )}
        </div>

        {/* Core Competencies */}
        <div>
          <ResumeSectionTitle className="mb-3">Core Competencies &amp; Expertise</ResumeSectionTitle>
          {data.skills.length > 0 ? (
            <div className="grid grid-cols-2 gap-2.5 text-xs sm:grid-cols-3">
              {data.skills.map((skill) => (
                <div
                  key={skill.id ?? skill.name}
                  className="flex items-center gap-2 rounded-xl border border-black/[0.04] bg-surface-2 px-3 py-2 font-medium text-ink-soft"
                >
                  <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-primary" />
                  <span className="truncate">{skill.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">No skills tagged yet.</p>
          )}
        </div>

        {/* Professional Experience */}
        <div>
          <ResumeSectionTitle className="mb-4">Professional Experience</ResumeSectionTitle>
          {experience.length > 0 ? (
            <div className="flex flex-col gap-6">
              {experience.map((item) => (
                <div key={item.id} className="flex flex-col gap-2">
                  <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center">
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-ink">
                        {item.job_title ? toTitleCase(item.job_title) : "—"}
                      </h3>
                      <p className="text-xs font-semibold text-primary">
                        {item.company_name ? toTitleCase(item.company_name) : ""}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-ink-faint sm:text-right">
                      {experiencePeriod(item)}
                    </span>
                  </div>
                  {item.description && (
                    <MarkdownContent
                      content={item.description}
                      className="text-xs leading-relaxed text-ink-soft [&_p]:mb-1.5 [&_p]:text-xs [&_p]:text-ink-soft [&_p]:last:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:text-xs [&_li]:text-ink-soft"
                    />
                  )}
                  {item.achievements && item.achievements.length > 0 && (
                    <ul className="list-disc space-y-1.5 pl-4 text-xs text-ink-muted">
                      {item.achievements.map((achievement, idx) => (
                        <li key={idx} className="leading-relaxed">
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">No work experience data available.</p>
          )}
        </div>

        {/* Education */}
        <div>
          <ResumeSectionTitle className="mb-4">Education &amp; Qualifications</ResumeSectionTitle>
          {data.education_items.length > 0 ? (
            <div className="flex flex-col gap-4">
              {data.education_items.map((item) => {
                const title = educationTitle(item);
                const school = educationSchool(item, title);
                const period = educationPeriod(item);
                return (
                  <div
                    key={item.id}
                    className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center"
                  >
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-ink">{title}</h3>
                      {school && <p className="text-xs text-ink-muted">{school}</p>}
                      {item.achievements && (
                        <p className="mt-0.5 text-[11px] text-primary">{item.achievements}</p>
                      )}
                    </div>
                    {period && (
                      <span className="shrink-0 text-xs font-medium text-ink-faint">{period}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">No education data available.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CvViewer — inline PDF viewer (ingest review page; profile Resume tab uses ResumeTemplate)
// ---------------------------------------------------------------------------

export interface CvViewerProps {
  candidateId: string;
  candidateName?: string;
}

// Zoom constants
const MIN_SCALE = 0.5;
const MAX_SCALE = 3.0;
const SCALE_STEP = 0.15;

export function CvViewer({ candidateId, candidateName }: CvViewerProps) {
  const { blobUrl, contentType, isLoading, isError, download } =
    useCandidateCv(candidateId);

  const isPdf = contentType?.includes("pdf") ?? false;
  const isDocx =
    contentType?.includes("wordprocessingml") ||
    contentType?.includes("vnd.openxmlformats") ||
    contentType?.includes("msword");

  const displayName = candidateName ? `${candidateName} Resume` : "Resume";

  // PDF viewer state
  const [numPages, setNumPages] = useState<number>(0);
  // currentPage is derived from scroll position — which page is most visible
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);

  // Ref to the scrollable container holding all pages
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Map of page number → wrapper div for scroll-based page detection
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  function handleDocumentLoadSuccess({ numPages: n }: { numPages: number }) {
    setNumPages(n);
    setCurrentPage(1);
  }

  function zoomIn() {
    setScale((prev) =>
      parseFloat(Math.min(prev + SCALE_STEP, MAX_SCALE).toFixed(2))
    );
  }

  function zoomOut() {
    setScale((prev) =>
      parseFloat(Math.max(prev - SCALE_STEP, MIN_SCALE).toFixed(2))
    );
  }

  // Scroll a specific page into view (used by bottom prev/next arrows)
  function scrollToPage(page: number) {
    if (page < 1 || page > numPages) return;
    const el = pageRefs.current.get(page);
    if (el && scrollContainerRef.current) {
      const containerTop =
        scrollContainerRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const offset =
        elTop - containerTop + scrollContainerRef.current.scrollTop;
      scrollContainerRef.current.scrollTo({ top: offset, behavior: "smooth" });
    }
  }

  // Scroll handler: determine which page occupies the most visible area
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages === 0) return;

    const containerRect = container.getBoundingClientRect();
    let bestPage = 1;
    let bestVisibleHeight = -1;

    pageRefs.current.forEach((el, pageNum) => {
      const rect = el.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, containerRect.top);
      const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      if (visibleHeight > bestVisibleHeight) {
        bestVisibleHeight = visibleHeight;
        bestPage = pageNum;
      }
    });

    setCurrentPage(bestPage);
  }, [numPages]);

  // Attach scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Format scale label: e.g. 1.0 → "1.0x", 1.5 → "1.5x"
  const scaleLabel = `${scale.toFixed(1)}x`;

  return (
    <Card padding="none" className="flex flex-col gap-0 overflow-hidden rounded-4xl">
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <p className="text-sm font-bold text-ink">{displayName}</p>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={DownloadMinimalisticLinear}
          disabled={!blobUrl || isLoading}
          onClick={() => download(candidateName)}
          aria-label="Download CV"
        >
          Download CV
        </Button>
      </div>

      {/* Content */}
      <div className="min-h-[480px]">
        {isLoading && (
          <div
            className="flex h-full min-h-[480px] items-center justify-center px-5 pb-5"
            aria-label="Loading CV"
          >
            <Spinner size={32} />
          </div>
        )}

        {!isLoading && isError && (
          <div className="flex h-full min-h-[480px] flex-col items-center justify-center gap-2 px-5 pb-5">
            <p className="text-sm font-medium text-ink-muted">No resume available</p>
          </div>
        )}

        {!isLoading && !isError && blobUrl && isPdf && (
          <div className="flex flex-col overflow-hidden">
            {/* Viewer body: left zoom strip + scrollable pages */}
            <div className="flex h-[680px] bg-surface-3">
              {/* Left zoom strip — 28px wide, 4px gap between items */}
              <div className="flex w-7 shrink-0 flex-col items-center gap-1 py-3 ml-5">
                {/* Zoom out button — white bordered box */}
                <button
                  type="button"
                  onClick={zoomOut}
                  disabled={scale <= MIN_SCALE}
                  aria-label="Zoom out"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-md border border-ink-subtle bg-white text-ink-muted shadow-card transition-colors",
                    scale <= MIN_SCALE
                      ? "cursor-not-allowed opacity-40"
                      : "cursor-pointer hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                  )}
                >
                  <Icon icon={MagnifierZoomOutLinear} size="sm" />
                </button>

                {/* Grey bar with zoom in control inside */}
                <div className="flex w-7 flex-col items-center justify-center gap-1 rounded-md bg-ink-subtle py-2">
                  <button
                    type="button"
                    onClick={zoomIn}
                    disabled={scale >= MAX_SCALE}
                    aria-label="Zoom in"
                    className={cn(
                      "flex size-5 items-center justify-center rounded-sm text-ink transition-colors",
                      scale >= MAX_SCALE
                        ? "cursor-not-allowed opacity-40"
                        : "cursor-pointer hover:bg-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                    )}
                  >
                    <Icon icon={MagnifierZoomInLinear} size="sm" />
                  </button>
                </div>

                {/* Zoom level label — white bordered box */}
                <div className="flex h-7 w-7 items-center justify-center rounded-md border border-ink-subtle bg-white">
                  <span className="text-[9px] font-semibold tabular-nums text-ink-muted">
                    {scaleLabel}
                  </span>
                </div>
              </div>

              {/* Scrollable all-pages area */}
              <div
                ref={scrollContainerRef}
                className="flex flex-1 overflow-auto"
                aria-label={`Page ${currentPage} of ${numPages}`}
              >
                <div className="flex min-w-full flex-col items-center py-6 px-4 gap-4">
                  <Document
                    file={blobUrl}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={
                      <div className="flex h-64 items-center justify-center">
                        <Spinner size={32} />
                      </div>
                    }
                    error={
                      <div className="flex h-64 items-center justify-center">
                        <p className="text-sm text-ink-muted">Failed to load PDF.</p>
                      </div>
                    }
                  >
                    {numPages > 0 &&
                      Array.from({ length: numPages }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <div
                            key={pageNum}
                            ref={(el) => {
                              if (el) pageRefs.current.set(pageNum, el);
                              else pageRefs.current.delete(pageNum);
                            }}
                            className="border border-ink-subtle shadow-pop"
                          >
                            <Page
                              pageNumber={pageNum}
                              scale={scale}
                              renderTextLayer
                              renderAnnotationLayer
                            />
                          </div>
                        );
                      })}
                  </Document>
                </div>
              </div>
            </div>

            {/* Bottom page indicator */}
            <div className="flex items-center justify-center bg-surface-3 py-2">
              <div className="flex items-center gap-1 rounded-full bg-ink px-3 py-1.5">
                <button
                  type="button"
                  onClick={() => scrollToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-white/80 transition-colors",
                    currentPage <= 1
                      ? "cursor-not-allowed opacity-40"
                      : "hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                  )}
                >
                  <Icon icon={AltArrowLeftLinear} size="sm" />
                </button>
                <span className="min-w-[64px] text-center text-xs font-medium text-white tabular-nums">
                  {numPages > 0 ? `${currentPage} / ${numPages}` : "— / —"}
                </span>
                <button
                  type="button"
                  onClick={() => scrollToPage(currentPage + 1)}
                  disabled={currentPage >= numPages}
                  aria-label="Next page"
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-white/80 transition-colors",
                    currentPage >= numPages
                      ? "cursor-not-allowed opacity-40"
                      : "hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                  )}
                >
                  <Icon icon={AltArrowRightLinear} size="sm" />
                </button>
              </div>
            </div>
          </div>
        )}

        {!isLoading && !isError && blobUrl && isDocx && (
          <div className="flex h-full min-h-[480px] flex-col items-center justify-center gap-4 px-5 pb-5">
            <p className="text-sm text-ink-muted">Preview not available for this file format.</p>
            <Button leadingIcon={DownloadMinimalisticLinear} onClick={() => download(candidateName)}>
              Download to view
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
